'use client'

import { getProgramInfo } from '@/lib/api/auth'
import { useCompanyContext } from '@/lib/providers/CompanyProvider'
import useLoadingStore from '@/lib/stores/loadingStore'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useParams, useRouter } from 'next/navigation'
import { useEffect, useMemo } from 'react'
import CTA from './_component/CTA'
import Header from '@/components/Common/Header'
import PioneersHeader from '@/containers/Event/KTechPioneers/_components/PioneersHeader'
import { domainRewriteMap } from '@/lib/config/domainRoutes'

/** KTP 전용 헤더(GNB)가 적용되는 기업 코드 */
const KTP_COMPANY_CODES = ['CMP000008']

const ProgramDetailContainer = () => {
  const router = useRouter()
  const { code } = useCompanyContext()
  const isKtpDomain = KTP_COMPANY_CODES.includes(code)
  const { setIsLoading } = useLoadingStore()

  const params = useParams()
  const id = params.id as string

  const {
    data: programInfo,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['getProgramInfo', id],
    queryFn: () => getProgramInfo(id),
    enabled: !!id,
    select: data => data.data,
    // Disable caching across unmounts and always refetch when possible
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })

  const programCode = programInfo?.program?.code || null

  const baseUrl = useMemo(() => {
    const cloudFront = process.env.AWS_S3_CLOUDFRONT_URL
    if (!cloudFront || !code || !programCode) return null
    return `https://${cloudFront}/cf/${code}/${programCode}/site/latest/index.html`
  }, [code, programCode])

  const iframeUrl = useMemo(() => {
    if (!baseUrl) return null
    return `${baseUrl}?v=${Date.now()}`
  }, [baseUrl])

  // Validate the base URL server-side (avoid CORS) before rendering iframe
  const { data: validation, isLoading: isValidating } = useQuery({
    queryKey: ['validateProgramSite', baseUrl],
    queryFn: async () => {
      const res = await fetch(`/api/site/validate?url=${encodeURIComponent(baseUrl as string)}`, {
        cache: 'no-store',
      })
      return res.json() as Promise<{ ok: boolean }>
    },
    enabled: !!baseUrl,
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  })

  const RenderCTA = useMemo(() => {
    if (!programInfo) return null
    const { application } = programInfo

    switch (true) {
      // 모집 예정
      case !application?.start_at || !application?.end_at:
        return <CTA type="applicationNone" />

      // startLearning
      case application?.submission?.status === 'passed':
        return <CTA type="startLearning" />

      // submissionComplete
      case application?.submission?.status === 'submitted' ||
        application?.submission?.status === 'failed' ||
        application?.submission?.status === 'canceled' ||
        application?.submission?.status === 'spared':
        return <CTA type="submissionComplete" />

      // applicationClosed
      case application?.end_at && dayjs(application?.end_at).isBefore(dayjs()):
        return <CTA type="applicationClosed" />

      // applicationBefore
      case application?.start_at && dayjs().isBefore(dayjs(application?.start_at)):
        return <CTA type="applicationBefore" startAt={application?.start_at} />

      // resumeApplication
      case application?.submission?.status === 'pending' &&
        application?.submission?.current_depth >= 0:
        return <CTA type="resumeApplication" />

      // applyForm
      default:
        return <CTA type="applyForm" endAt={application?.end_at} />
    }
  }, [programInfo])

  useEffect(() => {
    setIsLoading(true)
  }, [])

  useEffect(() => {
    if (!isLoading && !isValidating) {
      setIsLoading(false)
    }
  }, [isLoading, isValidating, setIsLoading])

  useEffect(() => {
    if (programInfo && !programInfo?.program?.is_apply) {
      void router.push('/')
    }
  }, [programInfo])

  useEffect(() => {
    if (isError) {
      router.replace('/404')
    }
  }, [isError, router])

  return (
    <div className={`h-full min-h-screen w-full ${typeof window !== 'undefined' && window.location.hostname in domainRewriteMap ? 'bg-black' : ''}`}>
      {isKtpDomain ? <PioneersHeader basePath="/" /> : <Header />}
      {/* PioneersHeader는 fixed 포지션이라 spacer 필요 */}
      {isKtpDomain && <div className="h-[60px] pc:h-[75px]" />}
      {iframeUrl && validation?.ok ? (
        <iframe
          src={iframeUrl}
          width="100%"
          style={{
            height: isKtpDomain
              ? 'calc(100vh - 60px)'
              : 'calc(100vh - 64px)',
            border: 'none',
          }}
          className={isKtpDomain ? 'pc:!h-[calc(100vh-75px)]' : ''}
        />
      ) : null}
      {programInfo?.program?.is_apply ? RenderCTA : null}
    </div>
  )
}
export default ProgramDetailContainer
